import { clamp } from 'lodash-es'
import pluralize from 'pluralize'
import { Dayjs, dayjs } from 'src/lib/dayjs'

import { ICall } from './types'

export function appendParamsToUrl(s: string, params: { [key: string]: string }): string {
  const url = new URL(s, location.origin)
  for (const [key, value] of Object.entries(params)) {
    url.searchParams.set(key, value)
  }
  return url.toString()
}

// Return -1 if a < b
// Return  0 if a = b
// Return  1 if a > b
export function compareSeconds(a: number, b: number, epsilon = 0.0001): number {
  if (a === b || Math.abs(a - b) < epsilon) {
    return 0
  } else if (a < b) {
    return -1
  } else {
    return 1
  }
}

export class CallTiming {
  milestones: Dayjs[] = []

  constructor(public call: ICall) {
    const duration = this.scheduledDuration
    const start = this.actualStart
    const end = this.scheduledEnd
    const increment = duration > 30 ? 10 : 5
    const milestoneEnd = end.subtract(5, 'm')
    // Add milestones until we've reached the last one
    for (
      let milestone = start.add(increment, 'm');
      milestone.isBefore(milestoneEnd);
      milestone = milestone.add(increment, 'm')
    ) {
      this.milestones.push(milestone)
    }
    // Add predefined milestones for call end
    this.milestones.push(
      // 5 minutes from end
      end.subtract(5, 'm'),
      // 2 minutes from end
      end.subtract(2, 'm'),
      // End
      end
    )
  }

  get nextMilestone() {
    return this.minsOver
      ? dayjs(this.scheduledEnd).add(Math.ceil(this.minsOver / 5) * 5, 'm')
      : this.milestones.find((m) => m.isAfter(this.now))
  }

  get nextMilestoneInMs() {
    return Math.ceil(this.nextMilestone.diff(dayjs(), 'ms', false))
  }

  get now() {
    return dayjs()
  }

  get scheduledStart() {
    return dayjs(this.call.started_at)
  }

  get actualStart() {
    return dayjs(this.call.recording.started_at)
  }

  get actualMinsElapsed() {
    return Math.floor(this.now.diff(this.actualStart, 'm'))
  }

  get timelineStart() {
    return this.actualStart.isAfter(this.scheduledStart) ? this.scheduledStart : this.actualStart
  }

  get scheduledEnd() {
    return this.scheduledStart.add(this.call.duration_minutes, 'minute')
  }

  get scheduledDuration() {
    return this.call.duration_minutes
  }

  get actualDuration() {
    return this.scheduledEnd.diff(this.timelineStart, 'minute')
  }

  get minsOver() {
    const diff = this.now.diff(this.scheduledEnd, 'minute', true)
    return diff > 0 ? Math.ceil(diff) : 0
  }

  get minsElapsed() {
    return this.now.diff(this.timelineStart, 'minute', true)
  }

  get minsLeft() {
    const diff = this.scheduledEnd.diff(this.now, 'minute', true)
    return Math.ceil(diff)
  }

  get minsUntilScheduled() {
    return Math.ceil(this.scheduledStart.diff(this.now, 'minute', true))
  }

  get pctElapsed() {
    return clamp(this.minsElapsed / this.actualDuration, 0, 1)
  }

  get pctOver() {
    return clamp(this.minsOver / this.actualDuration, 0, 1)
  }

  get timeUntilScheduledStart() {
    const now = this.now
    const seconds = Math.ceil(this.scheduledStart.diff(now, 's', true))
    const minutes = Math.floor(this.scheduledStart.diff(now, 'm', true))
    const hours = Math.floor(this.scheduledStart.diff(now, 'h', true))
    const days = Math.floor(this.scheduledStart.diff(now, 'd', true))

    if (days > 0) {
      return pluralize('day', days, true)
    } else if (hours > 0) {
      return pluralize('hr', hours, true)
    } else if (minutes > 0) {
      return pluralize('min', minutes, true)
    } else if (seconds > 0) {
      return pluralize('sec', seconds, true)
    } else {
      return ''
    }
  }
}
